import { useEffect, useState } from 'react';
import ApiDatabase from 'server';
import UseContext from 'hooks/useContext';
import useModal from 'components/modal/useModal';
import Usefetchdata from 'hooks/usefetchdata';

const HistoryContent = () => {
  const token = localStorage.getItem('xsrfToken');
  const { setModalData } = UseContext();
  const { userData } = Usefetchdata();
  const { toggle } = useModal();
  const [payslipLogs, setPayslipLogs] = useState([]);
  const [invoiceLogs, setInvoiceLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [isWaiting, setIsWaiting] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');

  const fetchHistory = () => {
    setIsWaiting(true);
    let done = 0;
    const checkDone = () => { if (++done === 2) setIsWaiting(false); };

    ApiDatabase.getPayslipImportHistory(
      { token, page: 1, limit: 200 },
      (data) => {
        setPayslipLogs(
          (data.logs || []).map((l) => ({ ...l, _source: 'payslip' }))
        );
        checkDone();
      },
      (err) => {
        if (err.name !== 'CanceledError') console.log(err);
        checkDone();
      }
    );

    ApiDatabase.getInvoiceImportHistory(
      { token, page: 1, limit: 200 },
      (data) => {
        setInvoiceLogs(
          (data.logs || []).map((l) => ({ ...l, _source: 'invoice' }))
        );
        checkDone();
      },
      (err) => {
        if (err.name !== 'CanceledError') console.log(err);
        checkDone();
      }
    );
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    const socket = ApiDatabase.getSocket();
    if (!socket) return;

    const adminInfo = userData?._id
      ? { firstname: userData.firstname, lastname: userData.lastname }
      : null;

    const prependLog = (source, data) => {
      if (!data?.log) return;
      const entry = {
        ...data.log,
        _source: source,
        idEmployee: adminInfo,
      };
      if (source === 'invoice') {
        setInvoiceLogs((prev) => [entry, ...prev]);
      } else {
        setPayslipLogs((prev) => [entry, ...prev]);
      }
    };

    const onInvoice = (data) => prependLog('invoice', data);
    const onPayslip = (data) => prependLog('payslip', data);

    socket.on('invoiceImportCompleted', onInvoice);
    socket.on('payslipImportCompleted', onPayslip);
    return () => {
      socket.off('invoiceImportCompleted', onInvoice);
      socket.off('payslipImportCompleted', onPayslip);
    };
  }, [userData]);

  const allLogs = [...payslipLogs, ...invoiceLogs].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  );

  const filteredLogs = typeFilter
    ? allLogs.filter((log) => log._source === typeFilter)
    : allLogs;

  const perPage = 20;
  const totalPages = Math.ceil(filteredLogs.length / perPage);
  const logsToShow = filteredLogs.slice((page - 1) * perPage, page * perPage);

  useEffect(() => {
    setPage(1);
  }, [typeFilter]);

  const actionLabel = (log) => {
    if (log._source === 'invoice') {
      return log.action === 'invoice_imported' ? 'Import facture' : 'Suppression facture';
    }
    return log.action === 'imported' ? 'Import paie' : 'Suppression paie';
  };

  const statusLabel = (status) => {
    const map = {
      pending: 'En attente',
      processing: 'En cours',
      completed: 'Terminé',
      failed: 'Échoué',
    };
    return map[status] || status;
  };

  const statusColor = (status) => {
    const map = {
      pending: 'text-yellow-600',
      processing: 'text-blue-600',
      completed: 'text-green-600',
      failed: 'text-red-600',
    };
    return map[status] || '';
  };

  const handleRowClick = (log) => {
    setModalData({ logId: log._id });
    toggle(
      log._source === 'invoice' ? 'invoice_import_detail' : 'payslip_import_detail'
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-semibold text-gray-700">
          Historique des actions
        </h3>
        <select
          className="h-9 pl-2 pr-3 border rounded-lg text-sm"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">Tous</option>
          <option value="payslip">Fiches de paie</option>
          <option value="invoice">Factures client</option>
        </select>
      </div>
      {isWaiting ? (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3, 4, 5].map((_, i) => (
            <div key={i} className="h-8 bg-gray-100 rounded" />
          ))}
        </div>
      ) : logsToShow.length === 0 ? (
        <p className="text-sm text-gray-400">Aucun historique.</p>
      ) : (
        <>
          <table className="w-full text-sm border rounded">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">Admin</th>
                <th className="p-2 text-left">Action</th>
                <th className="p-2 text-right">Total</th>
                <th className="p-2 text-right">Succès</th>
                <th className="p-2 text-right">Échoué</th>
                <th className="p-2 text-right">Montant</th>
                <th className="p-2 text-left">Statut</th>
              </tr>
            </thead>
            <tbody>
              {logsToShow.map((log) => (
                <tr
                  key={log._id}
                  className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowClick(log)}
                >
                  <td className="p-2">
                    {log.timestamp
                      ? new Date(log.timestamp).toLocaleString('fr-FR')
                      : '-'}
                  </td>
                  <td className="p-2">
                    {log.idEmployee?.firstname} {log.idEmployee?.lastname}
                  </td>
                  <td className="p-2">{actionLabel(log)}</td>
                  <td className="p-2 text-right">{log.summary?.total || 0}</td>
                  <td className="p-2 text-right">
                    {log.summary?.success || 0}
                  </td>
                  <td className="p-2 text-right">
                    {log.summary?.failed || 0}
                  </td>
                  <td className="p-2 text-right">
                    {log.summary?.totalAmount != null
                      ? `${Number(log.summary.totalAmount).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
                      : '-'}
                  </td>
                  <td className={`p-2 font-medium ${statusColor(log.status)}`}>
                    {statusLabel(log.status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex gap-2 justify-center text-sm">
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Précédent
              </button>
              <span className="px-3 py-1">
                {page} / {totalPages}
              </span>
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HistoryContent;
